import React from 'react';
import { Box, Card, CardContent, LinearProgress, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import type { WalletInfo } from '../../types/usage';
import { formatTokens, formatUsd } from '../../services/usage.service';

interface Props { wallet: WalletInfo | undefined; isLoading: boolean; isError?: boolean }

const stateColor = (state?: string): 'success' | 'warning' | 'error' =>
  state === 'exhausted' ? 'error' : state === 'warning' ? 'warning' : 'success';

export const WalletCard: React.FC<Props> = ({ wallet, isLoading, isError }) => {
  if (!isLoading && isError) {
    return (
      <Card><CardContent>
        <Typography variant="h6">AI balance</Typography>
        <Typography color="error">Couldn't load balance. Try refreshing.</Typography>
      </CardContent></Card>
    );
  }
  if (isLoading || !wallet) {
    return (
      <Card><CardContent><Skeleton data-testid="wallet-skeleton" variant="rectangular" height={120} /></CardContent></Card>
    );
  }
  if (wallet.unavailable) {
    return (
      <Card><CardContent>
        <Typography variant="h6">AI balance</Typography>
        <Typography color="text.secondary">Balance unavailable — the billing service could not be reached. Usage continues to be recorded.</Typography>
      </CardContent></Card>
    );
  }
  const periodEnd = wallet.period_end ? new Date(wallet.period_end).toLocaleDateString() : null;
  const pct = wallet.used_pct ?? 0;

  return (
    <Card>
      <CardContent>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems={{ md: 'center' }}>
          <Box sx={{ minWidth: 200 }}>
            <Typography variant="overline" color="text.secondary">AI balance available</Typography>
            <Typography variant="h3" sx={{ fontWeight: 700 }}>
              {wallet.unlimited ? 'Unlimited' : formatUsd(wallet.available_usd ?? 0)}
            </Typography>
            {!wallet.unlimited && wallet.tokens_left_estimate != null && (
              <Tooltip title="Estimate at your organization's recent blended rate; the balance is in USD.">
                <Typography variant="body2" color="text.secondary">≈ {formatTokens(wallet.tokens_left_estimate)} tokens (estimate)</Typography>
              </Tooltip>
            )}
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            {!wallet.unlimited && (
              <>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, pct)}
                  color={stateColor(wallet.state)}
                  aria-valuenow={Math.round(pct)}
                  sx={{ height: 10, borderRadius: 5, mb: 1 }}
                />
                <Typography variant="body2" color={wallet.state === 'ok' ? 'text.secondary' : `${stateColor(wallet.state)}.main`}>
                  {wallet.state === 'exhausted' ? 'Balance exhausted — new sessions are blocked' : `${pct}% used`}
                </Typography>
              </>
            )}
            <Typography variant="body2" color="text.secondary">
              Allowance {formatUsd(wallet.allowance_remaining_usd ?? 0)} of {formatUsd(wallet.allowance_usd ?? 0)} left
              {periodEnd ? ` · resets ${periodEnd}` : ''}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Extra balance {formatUsd(wallet.extra_remaining_usd ?? 0)} (carries over)
            </Typography>
          </Box>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>Sources: QA Agent</Typography>
      </CardContent>
    </Card>
  );
};

export default WalletCard;
